import { Box, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { logError } from "@verivest/shared-components/src/error-handling/logError";
import dasherize from "dasherize";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import WizardContainer from "../../../../../components/wizards/Container";
import usePlacementsApi from "../../../../../hooks/usePlacementsApi";
import RightPanelInvestment from "../RightPanelInvestment";

const AccountRadio = React.forwardRef(
  ({ type, title, description, ...rest }, ref) => {
    return (
      <Radio
        ref={ref}
        colorScheme="brand"
        alignItems="flex-start"
        size="lg"
        value={type}
        mb="16px"
        {...rest}
      >
        <Box ml="4px">
          <Text fontWeight="700" lineHeight="24px" fontSize="16px" mb="4px">
            {title}
          </Text>
          <Text lineHeight="24px" fontSize="16px" color="text.grey">
            {description}
          </Text>
        </Box>
      </Radio>
    );
  },
);

AccountRadio.displayName = "AccountRadio";

const getTypeFromMethod = (method) => {
  return method === "Check" || method === "Bank Transfer"
    ? "Receive"
    : method === "Split Distribution"
    ? "Split"
    : method || -1;
};

const TypeSelector = ({
  basePath,
  onSkip,
  onBack,
  investment,
  distributionPreference,
  asDrawer,
  onContinue,
  onClose,
  customAnswers,
  noAnimation,
  title = "How would you like your distributions?",
  receiveDescription = "Any distributions for this investment will be sent to you. This is often referred to as an “income” investment. Distributions can be made by check or bank transfer.",
  reinvestDescription = 'Any distributions for this investment will be reinvested in the offering. This is often referred to as a "growth" investment.',
  splitDescription = "Any distributions for this investment may be split between the available distribution methods, including multiple bank accounts, on an amount or percent basis",
}) => {
  const [selected, setSelected] = useState(
    getTypeFromMethod(distributionPreference?.distributionMethod),
  );
  useEffect(() => {
    setSelected(getTypeFromMethod(distributionPreference?.distributionMethod));
  }, [distributionPreference?.distributionMethod]);

  const [error, setError] = useState();
  const placementsApi = usePlacementsApi();
  const history = useHistory();
  const onSubmit = async () => {
    try {
      if (!selected || selected === -1) {
        setError("You need to make a choice");
      }

      if (selected === "Reinvest") {
        if (distributionPreference && distributionPreference.id) {
          const distData = dasherize({
            id: distributionPreference.id,
            distributionMethod: selected,
            method1Value: null,
            method2Value: null,
          });
          await placementsApi.updateResource(
            "distribution-preference",
            distData,
          );
        } else {
          const distData = dasherize({
            distributionMethod: selected,
            method1Value: null,
            method2Value: null,
            account: { id: investment.account.id },
            offering: { id: investment.offering.id },
          });
          await placementsApi.createResource(
            "distribution-preference",
            distData,
          );
        }

        if (onContinue) return onContinue(true, "");
        return history.push(`${basePath}/progress`);
      }

      if (onContinue) return onContinue(false, (selected || "").toLowerCase());
      return history.push(`${basePath}/distribution/${selected.toLowerCase()}`);
    } catch (error) {
      logError(error);
    }
  };

  const offering = investment.offering;

  return (
    <WizardContainer
      title={title}
      onSubmit={onSubmit}
      customAnswers={customAnswers}
      rightExtra={<RightPanelInvestment investment={investment} />}
      message={error}
      onSkip={onSkip}
      onBack={onBack}
      asDrawer={asDrawer}
      drawerNoAnimation={noAnimation}
      onClose={onClose}
      submitText={selected === "Reinvest" && asDrawer ? "Save" : "Continue"}
    >
      <RadioGroup onChange={setSelected} defaultValue="-1" value={selected}>
        <Stack spacing="16px">
          {(offering.allowsBank || offering.allowsCheck) && (
            <AccountRadio
              title="Receive"
              description={receiveDescription}
              type="Receive"
            />
          )}
          {offering.allowsReinvest && (
            <AccountRadio
              title="Reinvest"
              description={reinvestDescription}
              type="Reinvest"
            />
          )}
          {offering.allowsSplitDistributions && (
            <AccountRadio
              title="Split"
              description={splitDescription}
              type="Split"
            />
          )}
        </Stack>
      </RadioGroup>
    </WizardContainer>
  );
};

export default TypeSelector;
